/** @fileoverview Chart block: its own card with the title, and the bars, donut or line the screen's charts read answered, or a loading, empty or error state. */

import type { CSSProperties } from 'react';
import { jsonPointerSegment } from '../../lib/previewSelection';

import { Skeleton } from '../ui/skeleton';

import type { FabrikChartComponent, FabrikChartData } from '../../types/apps';
import {
  CHART_ERROR_TEXT,
  chartColorOf,
  formatExact,
  plotHeightFor,
  truncatedNote,
} from './chartFormat';
import { ColumnPlot, DonutPlot, LinePlot, RowPlot, useElementWidth } from './chartPlots';
import { SAVE_APP_NOTE } from './helpers';
import InlineAlert from './InlineAlert';
import { useScreenCharts } from './ScreenChartsProvider';

/* Bars turn sideways past this many categories, or when names are longer than this. */
const MAX_COLUMN_CATEGORIES = 10;
const MAX_COLUMN_LABEL_CHARS = 14;
/* Narrowest a vertical bar's slot may be before the bars turn sideways. */
const MIN_COLUMN_SLOT = 48;

/**
 * Tells whether bars read better sideways: many categories, long names, or too little room.
 * @param data the chart's numbers
 * @param width the plot width in pixels
 */
const prefersRows = (data: FabrikChartData, width: number): boolean => {
  const { categories } = data;
  return (
    categories.length > MAX_COLUMN_CATEGORIES ||
    categories.some(({ label }) => label.length > MAX_COLUMN_LABEL_CHARS) ||
    width / categories.length < MIN_COLUMN_SLOT
  );
};

/**
 * Names of the series of a split chart, each with its color; nothing for a single series.
 * @param props the chart's numbers and type
 */
const SeriesLegend = ({
  data,
  type,
  selectionPath,
}: {
  data: FabrikChartData;
  type: FabrikChartComponent['type'];
  selectionPath?: string;
}) =>
  data.series.length > 1 ? (
    <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1 type-caption text-muted-foreground">
      {data.series.map(({ key, label }, seriesIndex) => (
        <li
          key={key}
          data-fabrik-selection-path={
            selectionPath
              ? `${selectionPath}/runtime/legend/series/${jsonPointerSegment(key)}`
              : undefined
          }
          data-fabrik-selection-definition-path={
            selectionPath
              ? `${selectionPath}/${data.series.length > 1 ? 'splitBy' : 'measure'}`
              : undefined
          }
          className="flex min-w-0 items-center gap-1.5"
        >
          <span
            className={`shrink-0 bg-(--chart-color) ${
              type === 'line' ? 'h-0.5 w-3 rounded-full' : 'size-2.5 rounded-xs'
            }`}
            style={{ '--chart-color': chartColorOf(key, seriesIndex) } as CSSProperties}
            aria-hidden="true"
          />
          <span className="truncate">{label}</span>
        </li>
      ))}
    </ul>
  ) : null;

/**
 * The chart's numbers as a table for screen readers, since the plot itself is only a picture.
 * @param props the chart's numbers, its measure and its title
 */
const ChartTable = ({
  data,
  measure,
  caption,
}: {
  data: FabrikChartData;
  measure: FabrikChartComponent['measure'];
  caption: string;
}) => {
  const { categories, series, values } = data;
  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          <th scope="col">Group</th>
          {series.map(({ key, label }) => (
            <th key={key} scope="col">
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {categories.map(({ key, label }, categoryIndex) => (
          <tr key={key}>
            <th scope="row">{label}</th>
            {series.map(({ key: seriesKey }, seriesIndex) => (
              <td key={seriesKey}>
                {formatExact(values[seriesIndex]?.[categoryIndex] ?? null, measure)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * Picks the plot for the chart type: bars upright or sideways, a donut, or a line.
 * @param props the chart, its numbers, and the width and height to use
 */
const ChartPlot = ({
  component,
  data,
  width,
  height,
  selectionPath,
}: {
  component: FabrikChartComponent;
  data: FabrikChartData;
  width: number;
  height: number;
  selectionPath?: string;
}) => {
  const { type, measure } = component;
  const plotProps = { data, measure, width, height, selectionPath };
  if (type === 'donut') {
    return <DonutPlot {...plotProps} />;
  }
  if (type === 'line') {
    return <LinePlot {...plotProps} />;
  }
  return prefersRows(data, width) ? <RowPlot {...plotProps} /> : <ColumnPlot {...plotProps} />;
};

/**
 * Placeholder bars while the first answer loads, the same height as the plot.
 * @param props the plot height
 */
const ChartSkeleton = ({ height }: { height: number }) => (
  <div
    className="flex h-(--plot-height) items-end gap-3 px-2"
    style={{ '--plot-height': `${height}px` } as CSSProperties}
    role="status"
  >
    {/* Bars of different heights, written out so their classes stay plain text. */}
    <Skeleton className="h-1/2 flex-1 rounded-b-none" aria-hidden="true" />
    <Skeleton className="h-4/5 flex-1 rounded-b-none" aria-hidden="true" />
    <Skeleton className="h-2/5 flex-1 rounded-b-none" aria-hidden="true" />
    <Skeleton className="h-full flex-1 rounded-b-none" aria-hidden="true" />
    <Skeleton className="h-3/5 flex-1 rounded-b-none" aria-hidden="true" />
    <Skeleton className="h-1/3 flex-1 rounded-b-none" aria-hidden="true" />
    <span className="sr-only">Loading…</span>
  </div>
);

/**
 * Plain line in the plot's place, for the empty state and the save-the-app note.
 * @param props the text to show
 */
const ChartMessage = ({ text }: { text: string }) => (
  <div className="flex min-h-30 items-center justify-center rounded-md border border-dashed border-border px-3 type-small text-muted-foreground">
    {text}
  </div>
);

/**
 * Draws one chart from the screen's charts read. Keeps the last answer on screen, faded, while a
 * new read runs after a filter change.
 * @param props the chart component
 */
export default function ChartRenderer({
  component,
  selectionPath,
}: {
  component: FabrikChartComponent;
  selectionPath?: string;
}) {
  const { id, title, type, measure, size } = component;
  const { isAvailable, isLoading, hasLoaded, results, error } = useScreenCharts();
  const [plotRef, width] = useElementWidth();
  const height = plotHeightFor(size);
  const result = results[id];

  /** The block's body for its current state. */
  const renderBody = () => {
    if (!isAvailable) {
      return <ChartMessage text={SAVE_APP_NOTE} />;
    }
    if (!hasLoaded) {
      return <ChartSkeleton height={height} />;
    }
    if (error) {
      return <InlineAlert variant="box">{error}</InlineAlert>;
    }
    if (!result || result.status === 'error') {
      const text =
        (result?.status === 'error' && CHART_ERROR_TEXT[result.code]) ||
        CHART_ERROR_TEXT.QUERY_FAILED;
      return <InlineAlert variant="box">{text}</InlineAlert>;
    }
    if (result.rowCount === 0 || result.categories.length === 0) {
      return <ChartMessage text="No data for these filters" />;
    }
    const note = truncatedNote(result, type);
    return (
      <>
        {type !== 'donut' && (
          <SeriesLegend data={result} type={type} selectionPath={selectionPath} />
        )}
        {width > 0 && (
          <ChartPlot
            component={component}
            data={result}
            width={width}
            height={height}
            selectionPath={selectionPath}
          />
        )}
        <ChartTable data={result} measure={measure} caption={title ?? 'Chart'} />
        {note && <p className="mt-3 type-caption text-muted-foreground">{note}</p>}
      </>
    );
  };

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      {title && (
        <div className="flex min-h-14 items-center border-b border-border px-4 py-2.5">
          <h3
            data-fabrik-selection-path={selectionPath ? `${selectionPath}/title` : undefined}
            className="truncate type-heading font-semibold text-card-foreground"
          >
            {title}
          </h3>
        </div>
      )}
      <div
        className={`min-w-0 px-4 py-4 transition-opacity ${
          isLoading && hasLoaded ? 'opacity-60' : ''
        }`}
        aria-busy={isLoading || undefined}
      >
        {/* Measured without the padding, so the plot fills the inner width. */}
        <div ref={plotRef} className="min-w-0">
          {renderBody()}
        </div>
      </div>
    </section>
  );
}
